import { app } from '../app';
import IPromise = angular.IPromise;

export interface ITranslationService {
    questionnaireUp(locales:string[]):IPromise<void>;
    questionnaireDown();

    sessionUp(locale:string);
    sessionDown();

    userUp(locale:string);
    userDown();

    getLanguages():ILanguage[];
    getCurrentLanguage():ILanguage;
    updateGlobalLanguage();
    setCurrentLanguage(language:ILanguage);
    bringBackPreviousLanguage();
    onLanguageChange(callback:() => void):() => void;

    getLanguageByLocale(locale:string):ILanguage;

    setAvailableLocales(locales:string[]);
    isCurrentLanguageAvailable():boolean;
    bringBackAllAvailableLocales();
    onAvailableLanguagesChange(callback:() => void):() => void;
}

/**
 * Language set importance:
 * - questionnaire (set of languages)
 * - user
 * - session
 */
export class TranslationService implements ITranslationService {

    private rootScope:IRootScope;
    private http:ng.IHttpService;
    private q:ng.IQService;
    private window:IWindowService;
    private translate:angular.translate.ITranslateService;
    private store:angular.a0.storage.INamespacedStoreService;

    private isQuestionnaireUp = false;
    private isSessionUp       = false;
    private isUserUp          = false;

    private previousLanguage:ILanguage;
    private currentLanguage:ILanguage;
    private languages:ILanguage[];
    private allAvailableLanguages:ILanguage[];

    private languageChangeCallbacks:(() => void)[]           = [];
    private availableLanguagesChangeCallbacks:(() => void)[] = [];

    private preferredLanguage:ILanguage = {
        country       : 'United Kingdom',
        countryNative : 'United Kingdom',
        language      : 'English',
        languageNative: 'English',
        locale        : 'en-GB',
        rtl           : false
    };

    private luminaProfile:any;

    constructor($rootScope:IRootScope,
                $http:ng.IHttpService,
                $q:ng.IQService,
                $window:IWindowService,
                $translate:angular.translate.ITranslateService,
                store:angular.a0.storage.INamespacedStoreService,
                luminaProfile:any) {

        this.rootScope = $rootScope;
        this.http      = $http;
        this.q         = $q;
        this.window    = $window;
        this.translate = $translate;
        this.store     = store;

        this.languages             = $window.languages;
        this.allAvailableLanguages = this.languages;

        this.luminaProfile = luminaProfile;
    }

    questionnaireUp(locales:string[]):IPromise<void> {
        return this.translate.onReady().then(() => {
            this.isQuestionnaireUp = true;

            if (locales) {
                this.setAvailableLocales(locales);
            }

            return this.q.resolve();
        });
    }

    questionnaireDown() {
        this.isQuestionnaireUp = false;
        this.bringBackAllAvailableLocales();
    }

    sessionUp(locale:string) {
        let language = this.getLanguageByLocale(locale);
        if (!language) {
            return;
        }

        this.isSessionUp = true;
        this.setCurrentLanguage(language);
    }

    sessionDown() {
        this.isSessionUp = false;
    }

    userUp(locale:string) {
        let language = this.getLanguageByLocale(locale);
        if (language == null) {
            return;
        }

        this.isUserUp = true;

        // doesn't matter if questionnaire is up or not
        return this.setCurrentLanguage(language);
    }

    userDown() {
        this.isUserUp = false;
    }

    getLanguages():ILanguage[] {
        return this.languages;
    }

    getCurrentLanguage():ILanguage {
        let locale = localStorage.getItem('locale');

        for (const language of this.languages) {
            if (language.locale == locale) {
                this.currentLanguage = language;
            }
        }

        return this.currentLanguage ? this.currentLanguage : this.preferredLanguage;
    }

    updateGlobalLanguage():ng.IPromise<any> {
        let profile = this.store.get('profile');

        const locale = this.getCurrentLanguage().locale;

        // if (profile.user_metadata.locale === locale) {
        //     return;
        // }

        profile.user_metadata.locale = locale;
        this.store.set('profile', profile);

        let ssoVersion = this.store.get('ssoVersion');

        if (ssoVersion && ssoVersion == '2') {
            return this.luminaProfile.saveKeycloakUserLanguage({
                preferredLanguage: locale
            }).$promise;
        } else {
            return this.luminaProfile.saveUserMetadata({
                locale: locale
            }).$promise;
        }
    }

    setCurrentLanguage(language:ILanguage) {
        this.previousLanguage = this.currentLanguage;
        this.currentLanguage  = language;

        this.store.set(this.translate.storageKey(), language.locale);


        return this.translate.onReady().then(() => {
            this.languageChangeCallbacks.forEach((callback) => callback());

            return this.translate.use(language.locale).then(() => {
                this.rootScope.language    = this.translate.use();
                this.rootScope.rtlLanguage = this.currentLanguage.rtl;

                angular.element('body').css('direction', (this.rootScope.rtlLanguage ? 'rtl' : 'ltr'));

                return this.http({
                    method: 'GET',
                    url   : '/api/resources/' + this.currentLanguage.locale
                }).then((value) => {
                    const data = value.data;

                    if (angular.isArray(data)) {
                        this.rootScope.languageBasedResources = data;
                    }
                });
            });
        });
    }

    onLanguageChange(callback:() => void):() => void {
        let index = this.languageChangeCallbacks.push(callback) - 1;

        return () => {
            this.languageChangeCallbacks.splice(index, 1);
        };
    }

    onAvailableLanguagesChange(callback:() => void):() => void {
        let index = this.availableLanguagesChangeCallbacks.push(callback) - 1;

        return () => {
            this.availableLanguagesChangeCallbacks.splice(index, 1);
        };
    }

    bringBackPreviousLanguage() {
        if (this.previousLanguage) {
            this.setCurrentLanguage(this.previousLanguage);
        }
    }

    getLanguageByLocale(locale:string):ILanguage {
        if (!this.languages || this.languages.length <= 0) {
            throw new Error('Languages not loaded!');
        }

        for (let language of this.languages) {
            if (language.locale === locale) {
                return language;
            }
        }

        return null;
    }

    setAvailableLocales(locales:string[]) {
        let languages = [];
        for (const language of this.languages) {
            if (locales.indexOf(language.locale) !== -1) {
                languages.push(language);
            }
        }

        this.allAvailableLanguages = this.languages;
        this.languages             = languages;
        this.availableLanguagesChangeCallbacks.forEach(callback => callback());

        if (!this.isCurrentLanguageAvailable()) {
            this.setCurrentLanguage(this.languages[0]);
        }
    }

    bringBackAllAvailableLocales() {
        this.languages = this.allAvailableLanguages;
    }

    isCurrentLanguageAvailable():boolean {
        for (const language of this.languages) {
            if (this.currentLanguage.locale === language.locale) {
                return true;
            }
        }

        return false;
    }

    static factory() {
        const factory = ($rootScope:IRootScope,
                         $http:ng.IHttpService,
                         $q:ng.IQService,
                         $window:IWindowService,
                         $translate:angular.translate.ITranslateService,
                         store:angular.a0.storage.INamespacedStoreService,
                         luminaProfile:any) =>
            new TranslationService($rootScope, $http, $q, $window, $translate, store, luminaProfile);
        factory.$inject = ['$rootScope', '$http', '$q', '$window', '$translate', 'store', 'luminaProfile'];
        return factory;
    }
}

app.factory('TranslationService', TranslationService.factory());